"use client";

import { createContext, useContext, useId, useCallback } from "react";
import { useControllableState } from "@radix-ui/react-use-controllable-state";

import type { ExpandingMenuContextProps, ExpandingMenuProps } from "./types/expanding-menu.types";

export const ExpandingMenuContext = createContext<ExpandingMenuContextProps>({
  value: "",
  disabled: false,
  expanded: false,
  toggleExpanded: () => {},
  onExpandedChange: () => {},
});

export const ExpandingMenuProvider: React.FC<ExpandingMenuProps> = (props) => {
  const id = useId();
  const {
    children,
    value = id,
    disabled,
    expanded: expandedProp,
    defaultExpanded: defaultExpandedProp = false,
    onExpandedChange,
  } = props;

  const handleExpandedChange = useCallback(
    (value: boolean | undefined) => {
      onExpandedChange?.(!!value);
    },
    [onExpandedChange],
  );

  const [expanded, setExpanded] = useControllableState<boolean | undefined>({
    defaultProp: defaultExpandedProp,
    prop: expandedProp,
    onChange: handleExpandedChange,
  });

  const toggleExpanded = useCallback(() => {
    setExpanded((prev) => !prev);
  }, [setExpanded]);

  return (
    <ExpandingMenuContext.Provider value={{ value, disabled, expanded, toggleExpanded, onExpandedChange }}>
      {children}
    </ExpandingMenuContext.Provider>
  );
};

export const useExpandingMenu = () => {
  return useContext(ExpandingMenuContext);
};
